import {useTranslation} from 'react-i18next';
import {Menu, Skeleton, Stack, Typography, MenuItem} from '@mui/material';
import {FiEdit} from 'react-icons/fi';
import {translations} from '../../../../../../i18n/translations';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {ScreenPaths} from '../../../../../../enums';
import {ActivitiesGroup} from '../../../../../../backend/interfaces/activities-group';
import {useParams} from 'react-router-dom';
import {useMemo, useState} from 'react';
import {EntityTemplate} from '../../../../../../backend/responses/entity-template';
import {Button} from '../../../../../../components/button/button';
import {Add} from '@mui/icons-material';
import {Pallete} from '../../../../../../theme/bluebird';
import {useObserveViewport} from '../../../../../../hooks';

type TemplatesHeaderProps = {
  template?: EntityTemplate;
  activitiesGroups?: ActivitiesGroup[];
  isLoading: boolean;
  name: string;
  description?: string;
  onActivitiesGroupSelected: (activitiesGroup: ActivitiesGroup) => void;
  onEditName: () => void;
};

export default function TemplatesHeader({
  template,
  activitiesGroups,
  isLoading,
  name,
  description,
  onActivitiesGroupSelected,
  onEditName,
}: TemplatesHeaderProps) {
  const {t} = useTranslation();
  const {isRunningMobile} = useObserveViewport();
  let {entityId}: {entityId: string} = useParams();
  const [addActivitiesGroupMenuAnchor, setAddActivitiesGroupMenuAnchor] = useState<
    undefined | HTMLElement
  >();
  const activitiesGroupList = useMemo(() => {
    if (!activitiesGroups) return [];

    let listedActivitiesGroups: string[] = [];

    template?.activitiesGroups?.map((template) => {
      if (!listedActivitiesGroups.includes(template.activitiesGroupId))
        listedActivitiesGroups.push(template.activitiesGroupId);
      return undefined;
    });

    return activitiesGroups.filter((group) => !listedActivitiesGroups.includes(group.id ?? ''));
  }, [activitiesGroups, template]);

  return (
    <GenericHeader
      isLoading={isLoading}
      titleCrumbs={
        isRunningMobile
          ? [
              {
                label: name,
                link: '',
              },
            ]
          : [
              {
                label: t(
                  translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                    .settings
                ),
                link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Settings}`,
              },
              {
                label: t(
                  translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                    .templates
                ),
                link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Settings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}`,
              },
              {
                label: name,
                link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Settings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}/${template?.id}`,
              },
            ]
      }
      actions={{
        actions: [
          {
            label: t(translations.office.professional_caregiver.settings.templates.edit_name),
            icon: {element: FiEdit},
            callback: onEditName,
          },
        ],
      }}
      leftColumn={
        isLoading ? (
          <Skeleton width={200} height={38} />
        ) : (
          <Typography
            sx={{
              fontWeight: 400,
              color: 'white',
              fontSize: '20px',
              lineHeight: '18px',
              display: '-webkit-box',
              textOverflow: 'ellipsis',
              WebkitLineClamp: '2',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
            }}
          >
            {`${t(translations.office.professional_caregiver.settings.templates.description)}: ${description}`}
          </Typography>
        )
      }
      rightColumn={
        <Stack
          direction="row"
          gap={2}
          sx={{
            justifyItems: 'flex-end',
            justifyContent: 'flex-end',
            alignContent: 'flex-end',
          }}
        >
          {isLoading ? (
            <></>
          ) : (
            <div className="add-activities-groups">
              {activitiesGroupList && (
                <>
                  <Button
                    aria-controls={addActivitiesGroupMenuAnchor ? 'add-activities-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={addActivitiesGroupMenuAnchor ? 'true' : undefined}
                    onClick={(event) => setAddActivitiesGroupMenuAnchor(event.currentTarget)}
                    variant="inverted"
                    size="large"
                    startIcon={<Add />}
                    disabled={activitiesGroupList.length === 0}
                    sx={{
                      width: isRunningMobile ? 'auto' : '146px',
                      height: '40px',
                      letterSpacing: '0.2px',
                      py: '8px',
                      '.MuiButton-startIcon': {
                        margin: isRunningMobile ? 0 : '0 8px 0 -4px',
                      },
                    }}
                  >
                    {!isRunningMobile &&
                      t(translations.seniors.edit.senior_dcm.careplan.activities.add_button)}
                  </Button>
                  <Menu
                    id="add-activities-menu"
                    anchorEl={addActivitiesGroupMenuAnchor}
                    open={!!addActivitiesGroupMenuAnchor}
                    onClose={() => setAddActivitiesGroupMenuAnchor(undefined)}
                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    sx={{
                      transformOrigin: 'bottom 20px',
                      width: '100%',
                      '.MuiList-root': {
                        padding: '4px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      },
                    }}
                  >
                    {activitiesGroupList.map((activitiesGroup: ActivitiesGroup) => {
                      if (!activitiesGroup.isActive) return undefined;

                      return (
                        <MenuItem
                          key={`activities-group-option-${activitiesGroup.id}`}
                          onClick={() => {
                            // Close Menu
                            setAddActivitiesGroupMenuAnchor(undefined);

                            // Set selected Activities Group
                            onActivitiesGroupSelected(activitiesGroup);
                          }}
                          value={activitiesGroup.id}
                          sx={{
                            height: '30px',
                            padding: '6px 10px',
                            flexDirection: 'row',
                            gap: '8px',
                            alignItems: 'center',
                            color: 'var(--dcm-gray)',
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '10px',
                            letterSpacing: '0em',
                            textAlign: 'center',
                            '&:hover': {
                              backgroundColor: Pallete.blue.lighter,
                            },
                          }}
                        >
                          {activitiesGroup.name}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}
            </div>
          )}
        </Stack>
      }
    />
  );
}
